import * as React from "react";
import classNames from "classnames";
import { withStyles } from "@material-ui/core/styles";
import withWidth, { isWidthDown } from "@material-ui/core/withWidth";
import { Breakpoint } from "@material-ui/core/styles/createBreakpoints";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/DeleteForever";
import AddIcon from "@material-ui/icons/Add";
import BackIcon from "@material-ui/icons/ArrowBack";
import FwdIcon from "@material-ui/icons/ArrowForward";
import VisibleIcon from "@material-ui/icons/Visibility";
import HiddenIcon from "@material-ui/icons/VisibilityOff";
import { lighten } from "@material-ui/core/styles/colorManipulator";
import UiButton from "./UiButton";
import { StyledTooltip } from "./StyledTooltip";

const styles = (theme: Theme) =>
  createStyles({
    button: {
      lineHeight: "27px",
    },
    hidden: {
      visibility: "hidden",
    },
    rightIcon: {
      marginLeft: theme.spacing.unit,
    },
    toolbar: {
      padding: theme.spacing.unit,
      textAlign: "right",
    },
    toolbarModern: {
      padding: 0,
      textAlign: "right",
    },
    withMinHeight: {
      minHeight: `calc(100vh - 80px)`,
    },
    btnDisabled: {
      opacity: 0.4,
    },
    actionsBar: {
      padding: "4px 10px",
      backgroundColor: lighten("#000", 0.98),
      borderRadius: "16px",
    },
    iconAction: {
      padding: 4,
      margin: "0 1px",
    },
    actionDivider: {
      borderLeft: `1px solid ${lighten("#000", 0.7)}`,
      height: "70%",
      margin: "0 4px",
      alignSelf: "center",
    },
  });

interface PropsTypes {
  navigateBackVisible: boolean;
  navigateFwdVisible: boolean;
  onNavigateBackClicked: any;
  onNavigateFwdClicked: any;
  navigationOnly?: boolean;
  motif?: boolean;
  classes: any;
  onSaveClicked?: any;
  onSkipClicked: any;
  onDeleteClicked?: any;
  someItemsHidden?: boolean;
  onToggleVisibility?: any;
  onAddClicked?: any;
  savingDisabled?: boolean;
  hiddenableShapeName?: string;
  width?: Breakpoint;
  selected?: boolean;
  nombreItems?: number;
  acceptLabel?: string;
  hideNavigation?: boolean;
  variant?: "default" | "segmentation2";
}

const NavButtons = ({
  classes,
  navigateBackVisible,
  navigateFwdVisible,
  onNavigateBackClicked,
  onNavigateFwdClicked,
}: any) => (
  <Grid item>
    <Grid container spacing={8} justify="center" alignItems="stretch">
      <Grid item style={{ flexGrow: 1 }}>
        <Button
          variant="contained"
          onClick={onNavigateBackClicked}
          title="Image précédente"
          className={classNames(classes.button, {
            [classes.btnDisabled]: !navigateBackVisible,
          })}
          disabled={!navigateBackVisible}
          fullWidth
        >
          <BackIcon />
        </Button>
      </Grid>
      <Grid item style={{ flexGrow: 1 }}>
        <Button
          variant="contained"
          onClick={onNavigateFwdClicked}
          title="Image suivante"
          className={classNames(classes.button, {
            [classes.btnDisabled]: !navigateFwdVisible,
          })}
          disabled={!navigateFwdVisible}
          fullWidth
        >
          <FwdIcon />
        </Button>
      </Grid>
    </Grid>
  </Grid>
);

const CanvasButtons = (props: PropsTypes) => {
  const {
    selected,
    classes,
    onSaveClicked,
    onSkipClicked,
    onDeleteClicked,
    onAddClicked,
    onNavigateBackClicked,
    onNavigateFwdClicked,
    navigateBackVisible,
    navigateFwdVisible,
    onToggleVisibility,
    someItemsHidden,
    nombreItems,
    motif,
    width,
    navigationOnly,
    savingDisabled,
    hiddenableShapeName,
    hideNavigation = false,
    variant = "default",
  } = props;

  const isModern = variant === "segmentation2";
  const toolbarClass = isModern ? classes.toolbarModern : classes.toolbar;
  const showUtilityActions =
    !navigationOnly && (onToggleVisibility || onAddClicked);

  const skipLabel = motif ? "Modifier motif" : "Passer cette image";
  const skipTooltip = "Passer cette image en choisissant un motif";

  if (isModern) {
    return (
      <Grid
        container
        direction="column"
        spacing={16}
        alignItems="stretch"
        className={toolbarClass}
        style={{ marginTop: 24 }}
      >
        {!hideNavigation && (
          <NavButtons
            classes={classes}
            navigateBackVisible={navigateBackVisible}
            navigateFwdVisible={navigateFwdVisible}
            onNavigateBackClicked={onNavigateBackClicked}
            onNavigateFwdClicked={onNavigateFwdClicked}
          />
        )}

        {showUtilityActions && (
          <Grid item xs={12}>
            <Grid
              container
              alignItems="center"
              justify="flex-start"
              className={classes.actionsBar}
            >
              {onToggleVisibility && (
                <StyledTooltip
                  title={`${
                    someItemsHidden ? "Montrer" : "Cacher"
                  } ${hiddenableShapeName}`}
                  placement="top"
                >
                  <span>
                    <IconButton
                      disabled={nombreItems === 0}
                      onClick={onToggleVisibility}
                      className={classes.iconAction}
                    >
                      {!someItemsHidden ? <VisibleIcon /> : <HiddenIcon />}
                    </IconButton>
                  </span>
                </StyledTooltip>
              )}
              {onToggleVisibility && onDeleteClicked && (
                <div className={classes.actionDivider} />
              )}
              {onAddClicked && (
                <StyledTooltip title="Ajouter un noeud" placement="top">
                  <span>
                    <IconButton
                      disabled={!selected}
                      onClick={onAddClicked}
                      className={classes.iconAction}
                    >
                      <AddIcon />
                    </IconButton>
                  </span>
                </StyledTooltip>
              )}
              {onDeleteClicked && (
                <StyledTooltip title="Supprimer la sélection" placement="top">
                  <span>
                    <IconButton
                      disabled={!selected}
                      onClick={onDeleteClicked}
                      className={classes.iconAction}
                      style={{ color: !selected ? undefined : "red" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </StyledTooltip>
              )}
            </Grid>
          </Grid>
        )}

        {onSkipClicked && (
          <Grid item xs={12}>
            <Grid container spacing={8} justify="flex-end">
              <Grid item>
                <StyledTooltip title={skipTooltip} placement="top">
                  <span>
                    <Button
                      color="secondary"
                      variant="outlined"
                      onClick={onSkipClicked}
                      className={classes.button}
                    >
                      {skipLabel}
                    </Button>
                  </span>
                </StyledTooltip>
              </Grid>
              {!navigationOnly && onSaveClicked && (
                <Grid item>
                  <StyledTooltip
                    title="Sauvegarder et passer à l'image suivante"
                    placement="top"
                  >
                    <span>
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={onSaveClicked}
                        className={classes.button}
                        disabled={savingDisabled}
                      >
                        Sauvegarder
                      </Button>
                    </span>
                  </StyledTooltip>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  }

  return (
    <Grid
      container
      direction="column"
      spacing={0}
      alignItems="stretch"
      className={classNames(classes.toolbar)}
    >
      <Grid item style={{ padding: 10, flexGrow: 1 }}>
        <Grid
          container
          direction="column"
          spacing={8}
          justify="space-between"
          alignItems="stretch"
        >
          {!hideNavigation && (
            <NavButtons
              classes={classes}
              navigateBackVisible={navigateBackVisible}
              navigateFwdVisible={navigateFwdVisible}
              onNavigateBackClicked={onNavigateBackClicked}
              onNavigateFwdClicked={onNavigateFwdClicked}
            />
          )}
          {showUtilityActions && (
            <Grid item>
              <Grid
                container
                direction="row"
                spacing={8}
                justify="center"
                alignItems="stretch"
              >
                <Grid item style={{ flexGrow: 1 }}>
                  <Button
                    variant="contained"
                    onClick={onToggleVisibility}
                    title={`${
                      someItemsHidden ? "Montrer" : "Cacher"
                    } ${hiddenableShapeName}`}
                    className={classNames(classes.button, {
                      [classes.btnDisabled]: nombreItems === 0,
                    })}
                    disabled={nombreItems === 0}
                    fullWidth
                  >
                    {!someItemsHidden ? <VisibleIcon /> : <HiddenIcon />}
                  </Button>
                </Grid>
                {onAddClicked && (
                  <Grid item style={{ flexGrow: 1 }}>
                    <Button
                      variant="contained"
                      onClick={onAddClicked}
                      title="Ajouter un noeud"
                      className={classNames(classes.button, {
                        [classes.btnDisabled]: !selected,
                      })}
                      disabled={!selected}
                      fullWidth
                    >
                      <AddIcon />
                    </Button>
                  </Grid>
                )}
                <Grid item style={{ flexGrow: 1 }}>
                  <Button
                    variant="contained"
                    onClick={onDeleteClicked}
                    className={classNames(classes.button, {
                      [classes.btnDisabled]: !selected,
                    })}
                    disabled={!selected}
                    title="Supprimer la sélection"
                    fullWidth
                  >
                    <DeleteIcon />
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid
        item
        style={{
          padding: `${
            width && isWidthDown("lg", width) ? "0.5" : "3"
          }em 1em 1em`,
          flexGrow: 1,
        }}
      >
        <Grid container direction="row" spacing={8} justify="flex-end">
          <Grid item>
            <UiButton
              color="secondary"
              variant="outlined"
              onClick={onSkipClicked}
              className={classes.button}
              title="Passer cette image"
            >
              {skipLabel}
            </UiButton>
          </Grid>
          {!navigationOnly && (
            <Grid item>
              <UiButton
                color="secondary"
                variant="contained"
                onClick={onSaveClicked}
                className={classes.button}
                title="Sauvegarder"
                disabled={savingDisabled}
              >
                Sauvegarder
              </UiButton>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

CanvasButtons.defaultProps = {
  navigationOnly: false,
  onToggleVisibility: null,
  onAddClicked: null,
  hideNavigation: false,
  variant: "default" as const,
};

export default withStyles(styles)(withWidth()(CanvasButtons));
